<template>
  <b-modal
    :visible="visible"
    title="ตรวจจากพิกัด"
    size="lg"
    scrollable
    no-close-on-backdrop
    @change="val => $emit('update:visible', val)"
  >
    <div v-if="preview">
      <small class="text-muted">{{ preview.lat }}, {{ preview.lng }}</small>

      <!-- ที่อยู่ -->
      <div class="mt-1">
        <b-form-checkbox
          v-model="useAddress"
          :disabled="!preview.admin_area"
        >
          <strong>ที่อยู่ (จังหวัด / อำเภอ / ตำบล / รหัสไปรษณีย์)</strong>
        </b-form-checkbox>

        <div
          v-if="preview.admin_area"
          class="ml-2 mt-25"
        >
          <b-badge :variant="confidenceVariant">
            {{ addressSource }}
          </b-badge>
          <div>
            {{ preview.admin_area.tambon_th }} · {{ preview.admin_area.amphure_th }} ·
            {{ preview.admin_area.province_th }}
            <span v-if="preview.admin_area.zip_code"> · {{ preview.admin_area.zip_code }}</span>
          </div>
          <small
            v-if="preview.admin_area.confidence === 'low'"
            class="text-danger"
          >
            เดาจากโครงการข้างเคียง — ตรวจก่อนใช้
          </small>
        </div>
        <div
          v-else
          class="ml-2 text-muted"
        >
          หาที่อยู่จากพิกัดนี้ไม่ได้
        </div>
      </div>

      <!-- โซน -->
      <div class="mt-2">
        <b-form-checkbox
          v-model="useZone"
          :disabled="!preview.zones.length"
        >
          <strong>โซน ({{ preview.zones.length }})</strong>
        </b-form-checkbox>
        <div
          v-if="preview.zones.length"
          class="ml-2 mt-25"
        >
          <b-badge
            v-for="z in preview.zones"
            :key="z.id"
            variant="light-primary"
            class="mr-25"
          >
            {{ z.name }}
          </b-badge>
        </div>
        <div
          v-else
          class="ml-2 text-muted"
        >
          ไม่มีโซนใดครอบพิกัดนี้ (โซนมี polygon เฉพาะในกรุงเทพฯ)
        </div>
      </div>

      <!-- สถานี -->
      <div class="mt-2">
        <b-form-checkbox
          v-model="useTransit"
          :disabled="!preview.transits.length"
        >
          <strong>BTS / MRT / Airport Link ({{ preview.transits.length }})</strong>
        </b-form-checkbox>

        <small
          v-if="preview.used_fallback"
          class="ml-2 text-warning d-block"
        >
          ไม่มีสถานีในรัศมี 2 กม. — ขยายเป็น 10 กม. แล้วเก็บประเภทละสถานีที่ใกล้ที่สุด
        </small>

        <b-table-simple
          v-if="preview.transits.length"
          small
          class="mt-25 ml-2"
        >
          <b-thead>
            <b-tr><b-th>ประเภท</b-th><b-th>สถานี</b-th><b-th class="text-right">
              ระยะ (ม.)
            </b-th><b-th class="text-right">
              เดิน (นาที)
            </b-th></b-tr>
          </b-thead>
          <b-tbody>
            <b-tr
              v-for="t in preview.transits"
              :key="t.type_code + '-' + t.id"
            >
              <b-td>{{ t.types }}</b-td>
              <b-td>{{ t.name }}</b-td>
              <b-td class="text-right">
                {{ t.distance }}
              </b-td>
              <b-td class="text-right">
                {{ t.duration }}
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
        <div
          v-else
          class="ml-2 text-muted"
        >
          ไม่มีสถานีในรัศมี 10 กม.
        </div>
      </div>

      <!-- สถานที่ใกล้เคียง: แสดงอย่างเดียว ระบบบันทึกให้เองตอนกดบันทึก -->
      <hr>
      <div>
        <strong>สถานที่ใกล้เคียงในรัศมี 2 กม. ({{ preview.nearby_total }})</strong>
        <div class="text-muted">
          <small>ระบบบันทึกให้เองตอนกดบันทึก ไม่ต้องเลือก</small>
        </div>

        <b-table-simple
          v-if="preview.nearby && preview.nearby.length"
          small
          class="mt-25"
        >
          <b-thead>
            <b-tr><b-th>หมวด</b-th><b-th class="text-right">
              จำนวน
            </b-th><b-th>ที่ใกล้ที่สุด</b-th><b-th class="text-right">
              ระยะ
            </b-th><b-th class="text-right">
              เดิน
            </b-th></b-tr>
          </b-thead>
          <b-tbody>
            <b-tr
              v-for="g in preview.nearby"
              :key="g.category"
            >
              <b-td>{{ categoryLabel[g.category] || g.category }}</b-td>
              <b-td class="text-right">
                {{ g.count }}
              </b-td>
              <b-td>{{ poiName(g.nearest) }}</b-td>
              <b-td class="text-right">
                {{ g.nearest.distance }} ม.
              </b-td>
              <b-td class="text-right">
                {{ g.nearest.duration }} น.
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
        <div
          v-else
          class="text-muted"
        >
          ไม่มีสถานที่ในฐานข้อมูลอยู่ในรัศมี 2 กม. (ตอนนี้เก็บเฉพาะ กทม. และปริมณฑล)
        </div>
      </div>
    </div>

    <template #modal-footer>
      <b-button
        variant="outline-secondary"
        @click="$emit('cancel')"
      >
        ยกเลิก
      </b-button>
      <b-button
        v-if="beforeSave"
        variant="outline-secondary"
        @click="$emit('skip')"
      >
        บันทึกตามที่กรอกเอง
      </b-button>
      <b-button
        variant="primary"
        :disabled="!useZone && !useTransit && !useAddress"
        @click="$emit('apply', { zone: useZone, transit: useTransit, address: useAddress })"
      >
        {{ beforeSave ? 'ใช้ค่านี้แล้วบันทึก' : 'ใช้ค่านี้' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BButton, BFormCheckbox, BBadge, BTableSimple, BThead, BTbody, BTr, BTh, BTd,
} from 'bootstrap-vue'
import { POI_CATEGORIES } from '@/views/poi/poi-list/usePoiList'

/**
 * ยืนยันค่าที่คำนวณจากพิกัด ก่อนเอาไปใส่ฟอร์ม (คู่กับ new-backend-v3/PinGeoPreviewDialog.vue)
 *
 * ⚠️ popup นี้ไม่เขียนอะไรลง DB — กด "ใช้ค่านี้" แล้วค่าจะไปอยู่ในฟอร์ม
 * การบันทึกจริงยังเป็นปุ่มบันทึกของฟอร์มเหมือนเดิม
 *
 * แต่ละหัวข้อติ๊กแยกกันได้เพราะความแม่นไม่เท่ากัน: โซนกับสถานีมาจากรูปทรงและระยะทางจริง
 * ส่วนตำบลบางทีเดามาจากโครงการเพื่อนบ้าน (match: neighbour) ซึ่งผิดได้ราว 1 ใน 5
 *
 * ⚠️ สถานที่ใกล้เคียงไม่มีให้ติ๊ก — ไม่ได้เก็บในฟอร์ม ระบบคำนวณลงตาราง place_poi ให้เองตอนบันทึก
 */
export default {
  components: {
    BModal, BButton, BFormCheckbox, BBadge, BTableSimple, BThead, BTbody, BTr, BTh, BTd,
  },
  props: {
    visible: { type: Boolean, default: false },
    preview: { type: Object, default: null },
    /* true = เปิดมาจากปุ่มบันทึก ปุ่มใน popup จึงเป็น "ใช้ค่านี้แล้วบันทึก" */
    beforeSave: { type: Boolean, default: false },
  },
  data() {
    return {
      useZone: true,
      useTransit: true,
      useAddress: true,
      categoryLabel: POI_CATEGORIES.reduce((acc, c) => ({ ...acc, [c.value]: c.text }), {}),
      matchLabel: {
        boundary: 'จากขอบเขตตำบลจริง',
        'zip+tambon': 'จากที่อยู่ Google (รหัสไปรษณีย์ + ตำบล)',
        'amphure+tambon': 'จากที่อยู่ Google (อำเภอ + ตำบล)',
        zip: 'จากรหัสไปรษณีย์',
        neighbour: 'เดาจากโครงการข้างเคียง',
      },
    }
  },
  computed: {
    addressSource() {
      const area = this.preview && this.preview.admin_area

      return area ? (this.matchLabel[area.match] || area.match) : ''
    },
    confidenceVariant() {
      const area = this.preview && this.preview.admin_area
      const map = {
        exact: 'light-success', high: 'light-success', medium: 'light-warning', low: 'light-danger',
      }

      return area ? (map[area.confidence] || 'light-secondary') : 'light-secondary'
    },
  },
  watch: {
    /* ติ๊กไว้เฉพาะหัวข้อที่มีข้อมูลจริง — ไม่งั้นกด "ใช้ค่านี้" แล้วโซนที่กรอกไว้จะโดนล้างทิ้งเปล่า ๆ */
    preview(value) {
      this.useZone = !!(value && value.zones && value.zones.length)
      this.useTransit = !!(value && value.transits && value.transits.length)
      this.useAddress = !!(value && value.admin_area)
    },
  },
  methods: {
    poiName(poi) {
      const name = (poi && poi.name) || {}

      return name.th || name.en || Object.values(name).find(Boolean) || '(ไม่มีชื่อ)'
    },
  },
}
</script>
